/**
 * Procedural background music — MapleStory-flavored.
 *
 * No audio files. The whole track is synthesized from a tiny note grid:
 *   - 4-bar major-key progression (Cmaj → Am → Fmaj → G7) — a classic
 *     melodic loop that resolves cleanly back to its tonic.
 *   - 8 melody notes per bar (eighth-note feel at 88 BPM)
 *   - 1 bass note per bar, held long
 *   - 1 chord-pad note per bar at the 5th, also held long
 *
 * Synthesis voices:
 *   - Melody: pure sine, soft attack, ~0.55s decay — a "music-box bell".
 *   - Bass:   sine + triangle layered, very low gain, ~3s decay — warm pad.
 *   - Pad:    sine at 5th of chord, half-bar attack — soft sustain shimmer.
 *
 * Scheduler: Web Audio's `currentTime`-based scheduling. A timer wakes up
 * every 50ms and schedules any notes whose start time falls within the
 * next 500ms. This is the standard "look-ahead" pattern (Chris Wilson's
 * "A Tale of Two Clocks") — robust against tab throttling.
 */

const BPM = 88;
const BEAT_DUR = 60 / BPM;       // ~0.682s per beat
const BEATS_PER_BAR = 8;          // 8th notes per bar
const SCHEDULER_LOOKAHEAD = 0.45; // sec
const SCHEDULER_INTERVAL = 50;    // ms

interface Bar {
  /** Root note for the bass voice (MIDI). */
  bass: number;
  /** Chord 5th for the pad voice (MIDI). */
  pad: number;
  /** Eight melody notes (MIDI). 0 = rest. */
  melody: [number, number, number, number, number, number, number, number];
}

// ── Section A (verse-y, ascending energy) ──
// 8-bar progression in C major (~42s at 88 BPM). Established theme.
const BARS_A: ReadonlyArray<Bar> = [
  // Cmaj7
  { bass: 48, pad: 55, melody: [72, 76, 79, 84, 79, 76, 72, 76] },
  // Am7
  { bass: 45, pad: 52, melody: [69, 72, 76, 81, 76, 72, 69, 72] },
  // Dm7
  { bass: 50, pad: 57, melody: [74, 77, 81, 84, 81, 77, 74, 77] },
  // G7
  { bass: 43, pad: 50, melody: [67, 71, 74, 79, 74, 71, 67, 71] },
  // Cmaj7 lift
  { bass: 48, pad: 55, melody: [84, 79, 76, 84, 79, 76, 0,  79] },
  // Fmaj7
  { bass: 41, pad: 48, melody: [77, 81, 84, 89, 84, 81, 77, 81] },
  // Em7
  { bass: 52, pad: 59, melody: [76, 79, 83, 79, 76, 71, 0,  74] },
  // G7sus
  { bass: 43, pad: 50, melody: [67, 72, 76, 79, 76, 72, 67, 72] },
];

// ── Section B (drifting, sparser — the "space-floating" variant) ──
// Same chords (preserves harmonic identity) but the melody breathes more,
// uses rests, and explores higher registers. Provides contrast so the loop
// never feels repetitive.
const BARS_B: ReadonlyArray<Bar> = [
  // Cmaj7 — opening with rests, dreamy
  { bass: 48, pad: 55, melody: [79, 76, 0,  84, 79, 0,  76, 72] },
  // Am7 — softer descending
  { bass: 45, pad: 52, melody: [76, 72, 0,  81, 76, 0,  72, 69] },
  // Dm7 — climbing line
  { bass: 50, pad: 57, melody: [77, 81, 84, 88, 84, 81, 77, 0]  },
  // G7 — turnaround with hold
  { bass: 43, pad: 50, melody: [79, 74, 71, 0,  74, 71, 0,  79] },
  // Cmaj9 — high-register lift
  { bass: 48, pad: 55, melody: [88, 91, 88, 84, 79, 84, 88, 84] },
  // Fmaj7 — peak point of the loop
  { bass: 41, pad: 48, melody: [84, 89, 93, 89, 84, 81, 77, 81] },
  // Em7 → gentle resolve
  { bass: 52, pad: 59, melody: [83, 79, 76, 0,  79, 76, 71, 76] },
  // G7sus — return home
  { bass: 43, pad: 50, melody: [76, 72, 67, 71, 74, 71, 67, 0]  },
];

const SECTIONS = [BARS_A, BARS_B];

/** Convert MIDI pitch (60=C4) to Hz. */
function midiToHz(m: number): number {
  return 440 * Math.pow(2, (m - 69) / 12);
}

export class Music {
  private ctx: AudioContext | null = null;
  private musicGain: GainNode | null = null;

  private playing = false;
  private nextNoteTime = 0;
  private noteIdx = 0;
  private barIdx = 0;
  /** Which section is currently playing — alternates A↔B every full loop. */
  private sectionIdx = 0;
  private schedulerTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Game-driven intensity, 0..1.
   *   0   = calm bed (melody + bass + pad only)
   *   0.3 = counter-melody fades in
   *   0.5 = kick drum activates on downbeats
   *   1.0 = full ensemble, all voices at peak gain
   *
   * The engine ramps this slowly from player mass so the music swells as the
   * player grows — classic vertical re-orchestration.
   */
  private intensity = 0;

  /** Active oscillator nodes — tracked so stop() can quickly silence them. */
  private liveOscs = new Set<OscillatorNode>();

  setIntensity(t: number): void {
    this.intensity = Math.max(0, Math.min(1, t));
    // Wider swell range — calm at 0.55, hero-moment at 1.05. The shift
    // adds about +5dB which the ear genuinely registers as "louder".
    if (this.musicGain) {
      const target = 0.55 + this.intensity * 0.50; // 0.55 → 1.05
      this.musicGain.gain.value = target;
    }
  }

  /**
   * Start the music. Connects the music gain to `dest` (usually the
   * AudioEngine's master gain). Safe to call repeatedly — idempotent.
   */
  start(ctx: AudioContext, dest: AudioNode): void {
    if (this.playing) return;
    this.ctx = ctx;
    this.musicGain = ctx.createGain();
    // Music bus's macro gain is set in AudioEngine; here we just provide
    // a clean unity-ish multiplier for the music submix itself.
    this.musicGain.gain.value = 0.55;
    this.musicGain.connect(dest);
    this.playing = true;
    this.nextNoteTime = ctx.currentTime + 0.15;
    this.noteIdx = 0;
    this.barIdx = 0;
    this.sectionIdx = 0;
    this.scheduler();
  }

  stop(): void {
    this.playing = false;
    if (this.schedulerTimer) {
      clearTimeout(this.schedulerTimer);
      this.schedulerTimer = null;
    }
    // Quickly silence any still-ringing oscillators.
    const now = this.ctx?.currentTime ?? 0;
    for (const osc of this.liveOscs) {
      try {
        const g = (osc as unknown as { _env?: GainNode })._env;
        if (g) {
          g.gain.cancelScheduledValues(now);
          g.gain.setValueAtTime(g.gain.value, now);
          g.gain.linearRampToValueAtTime(0, now + 0.08);
        }
        osc.stop(now + 0.1);
      } catch { /* already stopped */ }
    }
    this.liveOscs.clear();
    if (this.musicGain) {
      this.musicGain.disconnect();
      this.musicGain = null;
    }
  }

  isPlaying(): boolean {
    return this.playing;
  }

  private scheduler = (): void => {
    if (!this.playing || !this.ctx || !this.musicGain) return;
    while (this.nextNoteTime < this.ctx.currentTime + SCHEDULER_LOOKAHEAD) {
      this.scheduleStep(this.nextNoteTime);
      this.nextNoteTime += BEAT_DUR;
    }
    this.schedulerTimer = setTimeout(this.scheduler, SCHEDULER_INTERVAL);
  };

  private scheduleStep(when: number): void {
    if (!this.ctx || !this.musicGain) return;
    const section = SECTIONS[this.sectionIdx]!;
    const bar = section[this.barIdx]!;
    const noteMidi = bar.melody[this.noteIdx]!;

    // ── Always-on bed: melody + bass + pad. ──
    if (noteMidi > 0) this.voiceMelody(when, noteMidi);
    if (this.noteIdx === 0) {
      this.voiceBass(when, bar.bass);
      this.voicePad(when, bar.pad);
    }

    // ── Counter-melody layer — fades in around intensity 0.15+. ──
    // Plays on the *offbeats* (1, 3, 5, 7) so it weaves between the main
    // melody. One octave above to add a higher harmony line.
    if (this.intensity > 0.15 && this.noteIdx % 2 === 1 && noteMidi > 0) {
      this.voiceCounter(when, noteMidi);
    }

    // ── Kick drum — engages at intensity 0.4+. Beats 1 + 5 of every bar.
    if (this.intensity > 0.40 && (this.noteIdx === 0 || this.noteIdx === 4)) {
      this.voiceKick(when);
    }

    // ── Sparkle 8th-note — engages at intensity 0.65+. Adds a running
    // bell on the half-beat between every note, doubling the perceived
    // rhythm without changing BPM. Gives the "fast moving" big-game feel.
    if (this.intensity > 0.65 && noteMidi > 0) {
      this.voiceSparkle(when + BEAT_DUR * 0.5, noteMidi);
    }

    // Advance pointers.
    this.noteIdx++;
    if (this.noteIdx >= BEATS_PER_BAR) {
      this.noteIdx = 0;
      this.barIdx = this.barIdx + 1;
      if (this.barIdx >= section.length) {
        this.barIdx = 0;
        // Section cycle: alternate A ↔ B every full 8-bar loop. The B
        // section preserves the same chord progression so the harmonic
        // identity stays intact, but the melody contour differs.
        this.sectionIdx = (this.sectionIdx + 1) % SECTIONS.length;
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Voices
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Melody voice — three layered sines:
   *   - fundamental at the note pitch
   *   - octave-up at -10dB for shimmer
   *   - slight detune (+6 cents) at -14dB for chorus thickness
   * The result is a "music-box bell" timbre that sits sweetly in the mix
   * and survives the reverb wash without smearing.
   */
  private voiceMelody(when: number, midi: number): void {
    const ctx = this.ctx!;
    const sink = this.musicGain!;
    const freq = midiToHz(midi);
    const dur = BEAT_DUR * 0.95;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(0.32, when + 0.018);
    env.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    env.connect(sink);

    const layers: Array<[number, number]> = [
      [freq, 1.0],           // fundamental
      [freq * 2, 0.30],      // octave up
      [freq * 1.0035, 0.22], // detuned twin (+6 cents)
    ];
    for (const [f, mul] of layers) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;
      const partial = ctx.createGain();
      partial.gain.value = mul;
      osc.connect(partial).connect(env);
      (osc as unknown as { _env: GainNode })._env = env;
      osc.start(when);
      osc.stop(when + dur + 0.05);
      this.trackOsc(osc, when + dur + 0.1);
    }
  }

  /**
   * Bass voice — fundamental sine + triangle fifth above, both attacked
   * softly so the low end "blooms" instead of thumping into the SFX. Held
   * through the full bar.
   */
  private voiceBass(when: number, midi: number): void {
    const ctx = this.ctx!;
    const sink = this.musicGain!;
    const dur = BEAT_DUR * BEATS_PER_BAR * 0.95;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(0.42, when + 0.10);
    env.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    env.connect(sink);

    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = midiToHz(midi);
    sub.connect(env);
    sub.start(when);
    sub.stop(when + dur + 0.1);

    const oct = ctx.createOscillator();
    const octGain = ctx.createGain();
    octGain.gain.value = 0.40;
    oct.type = 'triangle';
    oct.frequency.value = midiToHz(midi + 12); // octave up — adds body
    oct.connect(octGain).connect(env);
    oct.start(when);
    oct.stop(when + dur + 0.1);

    (sub as unknown as { _env: GainNode })._env = env;
    this.trackOsc(sub, when + dur + 0.2);
    this.trackOsc(oct, when + dur + 0.2);
  }

  /**
   * Pad voice — two sines (chord 5th + 3rd above) layered for a warm,
   * almost-string sustain. Slow attack so it doesn't poke through the beat.
   */
  private voicePad(when: number, midi: number): void {
    const ctx = this.ctx!;
    const sink = this.musicGain!;
    const dur = BEAT_DUR * BEATS_PER_BAR * 0.95;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(0.18, when + BEAT_DUR * 2);
    env.gain.linearRampToValueAtTime(0.0001, when + dur);
    env.connect(sink);

    const oFifth = ctx.createOscillator();
    oFifth.type = 'sine';
    oFifth.frequency.value = midiToHz(midi);
    oFifth.connect(env);
    oFifth.start(when);
    oFifth.stop(when + dur + 0.1);

    const oThird = ctx.createOscillator();
    const thirdGain = ctx.createGain();
    thirdGain.gain.value = 0.55;
    oThird.type = 'sine';
    // Major 3rd above the 5th = 7th (chord extension); reads sweet and open.
    oThird.frequency.value = midiToHz(midi + 4);
    oThird.connect(thirdGain).connect(env);
    oThird.start(when);
    oThird.stop(when + dur + 0.1);

    (oFifth as unknown as { _env: GainNode })._env = env;
    this.trackOsc(oFifth, when + dur + 0.2);
    this.trackOsc(oThird, when + dur + 0.2);
  }

  /**
   * Counter-melody voice — soft sine an octave above the melody note,
   * shorter sustain so it answers the lead instead of competing. Gain
   * scales with intensity so it eases in/out cleanly.
   */
  private voiceCounter(when: number, midi: number): void {
    const ctx = this.ctx!;
    const sink = this.musicGain!;
    const dur = BEAT_DUR * 0.55;
    const env = ctx.createGain();
    // Linear ramp: above threshold the voice is immediately audible at
    // base gain, then it climbs with intensity. (Quadratic kept it nearly
    // inaudible until very high intensity — the user couldn't hear it.)
    const intT = Math.max(0, Math.min(1, (this.intensity - 0.15) / 0.85));
    const intGain = 0.20 + 0.18 * intT; // 0.20 → 0.38 across the range
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(intGain, when + 0.015);
    env.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    env.connect(sink);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = midiToHz(midi + 12); // octave up
    osc.connect(env);
    osc.start(when);
    osc.stop(when + dur + 0.05);
    (osc as unknown as { _env: GainNode })._env = env;
    this.trackOsc(osc, when + dur + 0.1);
  }

  /**
   * Kick drum — short sine boom with a fast pitch drop. Activates at
   * intensity ≥ 0.5, beats 1 and 5. Gives the high-mass moments urgency.
   */
  private voiceKick(when: number): void {
    const ctx = this.ctx!;
    const sink = this.musicGain!;
    const dur = 0.14;
    const env = ctx.createGain();
    // Above threshold, kick is immediately present. Ramps up further with mass.
    const intT = Math.max(0, Math.min(1, (this.intensity - 0.40) / 0.60));
    const intGain = 0.38 + 0.20 * intT; // 0.38 → 0.58
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(intGain, when + 0.005);
    env.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    env.connect(sink);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, when);
    osc.frequency.exponentialRampToValueAtTime(46, when + 0.06);
    osc.connect(env);
    osc.start(when);
    osc.stop(when + dur + 0.05);
    (osc as unknown as { _env: GainNode })._env = env;
    this.trackOsc(osc, when + dur + 0.1);
  }

  /**
   * Sparkle voice — short, bright sine on the half-beat. Doubles the
   * apparent rhythm without changing BPM. Activates at intensity ≥ 0.65
   * and fades up gently across the upper range.
   *
   * Pitched one octave above the melody note for shimmer.
   */
  private voiceSparkle(when: number, midi: number): void {
    const ctx = this.ctx!;
    const sink = this.musicGain!;
    const dur = BEAT_DUR * 0.32;
    const env = ctx.createGain();
    const intT = Math.max(0, Math.min(1, (this.intensity - 0.65) / 0.35));
    const intGain = 0.10 + 0.10 * intT; // 0.10 → 0.20
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(intGain, when + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    env.connect(sink);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = midiToHz(midi + 12); // octave up
    osc.connect(env);
    osc.start(when);
    osc.stop(when + dur + 0.05);
    (osc as unknown as { _env: GainNode })._env = env;
    this.trackOsc(osc, when + dur + 0.1);
  }

  /** Register a finished-by time so we can drop the reference. */
  private trackOsc(osc: OscillatorNode, finishedBy: number): void {
    this.liveOscs.add(osc);
    const ms = Math.max(50, (finishedBy - (this.ctx?.currentTime ?? 0)) * 1000 + 50);
    setTimeout(() => this.liveOscs.delete(osc), ms);
  }
}
